import { useEffect, useState } from "react";
import { useStoreWrapper, useTableFiltersWrapper } from "../../../store/store";
import { shallow } from "zustand/shallow";
import useIncident from "../../../hooks/useIncident";
import { usePermissionCheck } from "../../../hooks/userPermissionCheck";
import { PERMISSION_LIST } from "../../../shared/sharedConstants";

const useDebounce = (value: any, delay: any) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const useIncidentList = (
  startDateParam?: any,
  endDateParam?: any
) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [tableData, setTableData] = useState<any>(null);
  const [allowCreateIncident, setAllowCreateIncident] = useState(false);

  const debouncedSearchQuery = useDebounce(searchQuery, 800);

  const {
    incidentStartDateFilter,
    incidentEndDateFilter,
    setIncidentEndDateFilter,
    setIncidentStartDateFilter,
  } = useTableFiltersWrapper((state) => ({
    incidentStartDateFilter: state.incidentStartDateFilter,
    incidentEndDateFilter: state.incidentEndDateFilter,
    setIncidentEndDateFilter: state.setIncidentEndDateFilter,
    setIncidentStartDateFilter: state.setIncidentStartDateFilter,
  }), shallow);

  const {
    userFacilities,
    userIncidentTypes,
    userIncidentStatus,
    setOpenIncidentClicked,
    user,
  } = useStoreWrapper((state) => ({
    createForm: state.createForm,
    updateForm: state.updateForm,
    userFacilities: state.userFacilities,
    userIncidentTypes: state.userIncidentTypes,
    userIncidentStatus: state.userIncidentStatus,
    setOpenIncidentClicked: state.setOpenIncidentClicked,
    user: state.user,
  }), shallow);

  const { getAllIncidentsWithParams, getIncidentDetailsWithId } = useIncident({
    userId: user.userId,
    searchParam: debouncedSearchQuery,
    startDate: incidentStartDateFilter,
    endDate: incidentEndDateFilter,
    });

  usePermissionCheck(
    PERMISSION_LIST.CREATE_INCIDENT,
    userFacilities,
    setAllowCreateIncident
  );

  useEffect(() => {
    // Guard against writing same value to store — Zustand set() always creates
    // new state even for identical values, triggering all subscribers.
    if (startDateParam && startDateParam !== incidentStartDateFilter) {
      setIncidentStartDateFilter(startDateParam);
    }
    if (endDateParam && endDateParam !== incidentEndDateFilter) {
      setIncidentEndDateFilter(endDateParam);
    }
  }, [
    startDateParam,
    endDateParam,
    incidentStartDateFilter,
    incidentEndDateFilter,
  ]);

  useEffect(() => {
    getAllIncidentsWithParams.refetch();
  }, [debouncedSearchQuery, incidentStartDateFilter, incidentEndDateFilter]);

  useEffect(() => {
    if (getAllIncidentsWithParams.data !== undefined) {
      const allIncidents =
        getAllIncidentsWithParams.isLoading || getAllIncidentsWithParams.error
          ? []
          : getAllIncidentsWithParams.data.data.data[0];
      setTableData(allIncidents);
    }
  }, [getAllIncidentsWithParams.data]);

  return {
    tableData,
    allowCreateIncident,
    searchQuery,
    setSearchQuery,
    userIncidentStatus,
    userIncidentTypes,
    isLoading:
      getAllIncidentsWithParams.isLoading ||
      getAllIncidentsWithParams.isFetching,
    debouncedSearchQuery,
    userFacilities,
    setOpenIncidentClicked,
    incidentEndDateQuery: incidentEndDateFilter,
    setIncidentEndDateQuery: setIncidentEndDateFilter,
  };
};

export { useDebounce, useIncidentList };
